import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DiscordSession } from "./discord";
import { initDiscord } from "./discord";
import { connectRoom, type RoomConnection } from "./ws";
import type { ClientMessage, RoomState, ServerMessage } from "./types";
import { Lobby } from "./components/Lobby";
import { ReferenceReveal } from "./components/ReferenceReveal";
import { DrawingCanvas } from "./components/DrawingCanvas";
import { VotingScreen } from "./components/VotingScreen";
import { Results } from "./components/Results";
import { GameOver } from "./components/GameOver";
import { Shop } from "./components/Shop";
import { Toast } from "./components/Toast";

type Stage = "boot" | "ready" | "error";

export function App() {
  const [stage, setStage] = useState<Stage>("boot");
  const [bootError, setBootError] = useState<string | null>(null);
  const [session, setSession] = useState<DiscordSession | null>(null);
  const [room, setRoom] = useState<RoomState | null>(null);
  const [you, setYou] = useState<string>("");
  const [status, setStatus] = useState<"connecting" | "open" | "closed">("connecting");
  const [shopOpen, setShopOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const connRef = useRef<RoomConnection | null>(null);

  useEffect(() => {
    let cancelled = false;
    initDiscord()
      .then((s) => {
        if (cancelled) return;
        setSession(s);
        setStage("ready");
      })
      .catch((err) => {
        console.error(err);
        if (cancelled) return;
        setBootError(err instanceof Error ? err.message : "Failed to launch.");
        setStage("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Open the WebSocket once the session is ready. Room code is derived from
  // the Discord instance id so everyone in the same channel/activity lands
  // in the same room automatically.
  useEffect(() => {
    if (!session) return;
    const code = roomCodeFromInstance(session.instanceId);
    const conn = connectRoom(
      code,
      (msg) => onServerMessage(msg),
      (s) => setStatus(s)
    );
    connRef.current = conn;
    return () => conn.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Send hello after the socket opens.
  useEffect(() => {
    if (!session || status !== "open") return;
    send({
      t: "hello",
      userId: session.user.id,
      name: session.user.globalName,
      avatar: session.user.avatar,
      cosmetics: session.cosmetics,
    });
  }, [session, status]);

  const onServerMessage = useCallback((msg: ServerMessage) => {
    if (msg.t === "state") {
      setRoom(msg.state);
      setYou(msg.you);
    } else if (msg.t === "error" || msg.t === "toast") {
      setToast(msg.message);
    }
  }, []);

  const send = useCallback((msg: ClientMessage) => {
    connRef.current?.send(msg);
  }, []);

  const me = useMemo(
    () => room?.players.find((p) => p.id === you) ?? null,
    [room, you]
  );

  if (stage === "boot") {
    return <SplashScreen subtitle="Tuning the whiskers..." />;
  }
  if (stage === "error") {
    return <SplashScreen subtitle={bootError ?? "Something went wrong."} />;
  }
  if (!session || !room || !me) {
    return <SplashScreen subtitle={statusLabel(status)} />;
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">≽^•⩊•^≼</span>
          <span className="brand-name">Catstrokes</span>
          <span className="brand-room">Room · {room.code}</span>
        </div>
        <div className="topbar-right">
          {me.cosmetics.catnipPack && <span className="pill pill-gold">Catnip</span>}
          <button className="ghost" onClick={() => setShopOpen(true)}>
            Shop
          </button>
        </div>
      </header>

      <main className="stage">
        {room.phase === "lobby" && (
          <Lobby
            room={room}
            you={you}
            onReady={(r) => send({ t: "ready", ready: r })}
            onStart={() => send({ t: "start" })}
            onKick={(id) => send({ t: "kick", targetId: id })}
          />
        )}
        {room.phase === "reveal" && <ReferenceReveal room={room} />}
        {room.phase === "drawing" && (
          <DrawingCanvas
            room={room}
            me={me}
            onSubmit={(image) => send({ t: "submit", image })}
          />
        )}
        {room.phase === "voting" && (
          <VotingScreen
            room={room}
            you={you}
            onVote={(id) => send({ t: "vote", targetId: id })}
          />
        )}
        {room.phase === "results" && (
          <Results
            room={room}
            you={you}
            onContinue={() => send({ t: "next" })}
          />
        )}
        {room.phase === "gameover" && (
          <GameOver
            room={room}
            you={you}
            onPlayAgain={() => send({ t: "next" })}
          />
        )}
      </main>

      {shopOpen && (
        <Shop
          session={session}
          onClose={() => setShopOpen(false)}
          onPurchased={() => {
            // After a successful purchase, refresh the server's view of cosmetics.
            send({
              t: "hello",
              userId: session.user.id,
              name: session.user.globalName,
              avatar: session.user.avatar,
              cosmetics: session.cosmetics,
            });
            setToast("Catnip Pack unlocked. Go wild.");
          }}
        />
      )}

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}

function statusLabel(s: "connecting" | "open" | "closed"): string {
  if (s === "open") return "Joining the cattery...";
  if (s === "closed") return "Reconnecting...";
  return "Connecting...";
}

function SplashScreen({ subtitle }: { subtitle: string }) {
  return (
    <div className="splash">
      <div className="splash-mark">≽^•⩊•^≼</div>
      <div className="splash-name">Catstrokes</div>
      <div className="splash-sub">{subtitle}</div>
    </div>
  );
}

/** Hash the instance id into a stable 5-char alphanumeric room code. */
function roomCodeFromInstance(instanceId: string): string {
  let h = 5381;
  for (const ch of instanceId) h = ((h << 5) + h + ch.charCodeAt(0)) >>> 0;
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 5; i++) {
    out += alphabet[h % alphabet.length];
    h = Math.floor(h / alphabet.length) + 7;
  }
  return out;
}
